/**
 * GallerySubNav - Segmented control navigation for gallery sub-routes.
 *
 * Renders a pill-style navigation that replaces traditional tabs.
 * Shows pending count badge on Review link when images need review.
 *
 * @see /src/features/gallery/layouts/GalleryLayout.tsx - Parent component
 */

import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Images, Eye, Briefcase, Download } from 'lucide-react'

interface GallerySubNavProps {
  /** Base path for gallery (e.g., /media/galleries/123) */
  basePath: string
  /** Number of pending images to display on Review badge */
  pendingCount?: number
  /** Current gallery status for conditional styling */
  galleryStatus?: 'pending' | 'approved' | 'rejected'
}

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  showBadge?: boolean
}

/**
 * Navigation items configuration.
 * Using end: true for index route matching.
 */
const NAV_ITEMS: NavItem[] = [
  { path: '', label: 'Overview', icon: Eye },
  { path: '/review', label: 'Review', icon: Images, showBadge: true },
  { path: '/job', label: 'Job Details', icon: Briefcase },
  { path: '/export', label: 'Export', icon: Download },
]

export function GallerySubNav({
  basePath,
  pendingCount = 0,
  galleryStatus,
}: GallerySubNavProps) {
  const location = useLocation()

  // Preserve current query params when navigating
  const currentSearch = location.search

  return (
    <div className="w-full overflow-x-auto pb-1 no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
      <nav
        role="tablist"
        aria-label="Gallery sections"
        className="inline-flex items-center gap-1 rounded-lg bg-muted/50 p-1 border border-border/50 min-w-max sm:min-w-0"
      >
        {NAV_ITEMS.map((item) => {
          const fullPath = `${basePath}${item.path}${currentSearch}`
          // Check if this is the active route
          // For index route (empty path), we need exact match
          const isActive = item.path === ''
            ? location.pathname === basePath || location.pathname === `${basePath}/`
            : location.pathname.startsWith(`${basePath}${item.path}`)

          const Icon = item.icon

          return (
            <NavLink
              key={item.path || 'index'}
              to={fullPath}
              role="tab"
              aria-selected={isActive}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{item.label}</span>

              {/* Badge for pending count on Review tab */}
              {item.showBadge && pendingCount > 0 && (
                <Badge
                  variant="warning"
                  className={cn(
                    'ml-1 px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                    'min-w-[1.25rem] justify-center',
                    isActive && 'bg-warning text-warning-foreground'
                  )}
                >
                  {pendingCount > 99 ? '99+' : pendingCount}
                </Badge>
              )}

              {/* Active indicator line */}
              {isActive && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                  aria-hidden="true"
                />
              )}
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}

/**
 * Compact version for mobile or narrow layouts.
 * Shows only icons with tooltips.
 */
export function GallerySubNavCompact({
  basePath,
  pendingCount = 0,
}: Pick<GallerySubNavProps, 'basePath' | 'pendingCount'>) {
  const location = useLocation()

  // Preserve current query params when navigating
  const currentSearch = location.search

  return (
    <nav
      role="tablist"
      aria-label="Gallery sections"
      className="inline-flex items-center gap-1 rounded-lg bg-muted/50 p-1 border border-border/50"
    >
      {NAV_ITEMS.map((item) => {
        const fullPath = `${basePath}${item.path}${currentSearch}`
        const isActive = item.path === ''
          ? location.pathname === basePath
          : location.pathname.startsWith(`${basePath}${item.path}`)

        const Icon = item.icon

        return (
          <NavLink
            key={item.path || 'index'}
            to={fullPath}
            role="tab"
            aria-selected={isActive}
            aria-label={item.label}
            title={item.label}
            className={cn(
              'relative flex items-center justify-center w-10 h-10 rounded-md transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            )}
          >
            <Icon className="h-5 w-5" />

            {/* Badge dot for pending on Review */}
            {item.showBadge && pendingCount > 0 && (
              <span
                className="absolute top-1 right-1 w-2 h-2 bg-warning rounded-full"
                aria-label={`${pendingCount} pending`}
              />
            )}
          </NavLink>
        )
      })}
    </nav>
  )
}
